<template>
  <div class="key-schedule-edit">
    <Sticky
      class="stick-save"
      extendStickyClass="stickyBtnClass"
      :fullWidth="false"
      :align="'right'"
    >
      <div class="btnGroup">
        <button
          v-if="!isEditing && allowGeneratePdf"
          class="btn--darkblue generatePDF"
          @click="generatePDF"
        >{{ $t('action.generatePDF') }}</button>
        <button
          v-if="!isEditing && allowEdit"
          class="btn--orange edit"
          @click="triggerEditStatus"
        >{{ $t('action.edit') }}</button>
        <button
          v-if="editMode"
          class="btn--darkblue cancel"
          @click="cancel"
        >{{ $t('action.cancel') }}</button>
        <button
          v-if="editMode"
          class="btn--orange save"
          @click="save"
        >{{ $t('action.save') }}</button>
      </div>
    </Sticky>
    <div class="key-header">
      <span class="remarks">
        <div class="key-title">{{ $t('handover.remark') }}</div>
        <i class="content">{{ $t(`terms.${regionName}.hotb.remark.${hotbType}`, [premises]) }}</i>
      </span>
    </div>
    <div class="content">
      <table>
        <thead>
          <tr>
            <th class="keyItem">
              {{ $t('item') }}
            </th>
            <th class="key">
              {{ $t('handover.keySchedule.keys') }}
            </th>
            <th class="keyHandedOver">
              {{ $t('handover.keySchedule.cards') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(item, index) in keyList"
            :key="index"
          >
            <th>
              <div class="handoverItem">
                <TextField
                  :required="item.keys !== '' || item.cards !== ''"
                  :showColon="false"
                  :readonly="!editMode"
                  :value="editMode ? item.keyItem : (item.keyItem || $t('emptyValue'))"
                  :inputTip="errorString"
                  :placeholder="$t('handover.keySchedule.placeholderMsg')"
                  @updateValue="val => changeItemValue(index, val, 'keyItem')"
                />
              </div>
            </th>
            <th>
              <div class="handoverKeys">
                <InputField
                  :readonly="!editMode"
                  :showColon="false"
                  :showFlag="false"
                  :value="editMode ? item.keys : (item.keys || $t('emptyValue'))"
                  :placeholder="$t('handover.keySchedule.placeholderEg6')"
                  :inputTip="errorNumber"
                  :inputCheckType="'integer'"
                  :pattern="regex.integer"
                  :required="isKeysRequired(index)"
                  @updateValue="val => changeItemValue(index, val, 'keys')"
                />
              </div>
            </th>
            <th class="keyHandedOver">
              <div class="handoverCards">
                <InputField
                  :readonly="!editMode"
                  :showColon="false"
                  :showFlag="false"
                  :value="editMode ? item.cards : (item.cards || $t('emptyValue'))"
                  :placeholder="$t('handover.keySchedule.placeholderEg6')"
                  :inputTip="errorNumber"
                  :inputCheckType="'integer'"
                  :pattern="regex.integer"
                  :required="isCardsRequired(index)"
                  @updateValue="val => changeItemValue(index, val, 'cards')"
                />
                <HKLDeleteIcon
                  v-if="editMode"
                  class="delete"
                  @click="() => openDeleteConfirmModel(item, index)"
                />
              </div>
            </th>
          </tr>
        </tbody>
      </table>
    </div>
    <DeleteConfirmModel ref="deleteKeyConfirmModel" />
    <div
      v-if="editMode"
      class="footer"
    >
      <i
        class="add"
        @click="addKeyListInner"
      >
        {{ $t('handover.keySchedule.addKeyItem') }}+
      </i>
    </div>
    <KeyScheduleSignature
      ref="keySignatureRef"
      :editStatus="editMode"
    />
  </div>
</template>

<script>
import lodash from 'lodash'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import DeleteConfirmModel from '@/controls/DeleteConfirmModel'
import InputField from '@/controls/InputField'
import Sticky from '@/controls/Sticky'
import TextField from '@/controls/TextField'
import { exportKeyHotb } from '@/api/printers/request'
import { fusionInputCheckUtil } from '@/plugin/FusionInputCheck'
import { utils as formUtils } from '@/plugin/InputChange'
import mutationTypes from '@/store/mutation-types'
import i18n from '@/utils/i18n'
import * as regex from '@/utils/regex'
import HKLDeleteIcon from '@/controls/Icon/HKLDeleteIcon.vue'
import KeyScheduleSignature from './KeyScheduleSignature'
export default {
  name: 'KeyScheduleEdit',
  components: {
    HKLDeleteIcon,
    TextField,
    InputField,
    Sticky,
    DeleteConfirmModel,
    KeyScheduleSignature,
  },
  props: {
    allowEdit: {
      type: Boolean,
      default: false,
    },
    allowGeneratePdf: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      errorString: i18n.t('hint.inputTip.string'),
      errorNumber: i18n.t('hint.inputTip.default'),
      regex,
      keyList: [],
      pdfId: null,
      isEditing: false,
    }
  },
  computed: {
    ...mapGetters('hotb', ['currentHotb', 'isHandover']),
    ...mapGetters('regions', ['regionName']),
    hotbType () {
      return this.currentHotb.type
    },
    premises () {
      return lodash.get(this.currentHotb, 'tenancy.building.fullName', '')
    },
    editMode () {
      return this.allowEdit && this.isEditing
    },
  },
  watch: {
    currentHotb () {
      if (this.currentHotb) {
        this.keyList = lodash.cloneDeep(lodash.get(this.currentHotb, 'transactions', []))
        if (lodash.isEmpty(this.keyList)) {
          this.addKeyListInner()
        }
      }
    },
  },
  methods: {
    ...mapActions('hotb', ['loadHotb', 'updateHotb']),
    ...mapMutations('hotb', {
      updateFieldByPath: mutationTypes.HOTB_UPDATE_FIELD_PATH,
    }),
    triggerEditStatus () {
      this.isEditing = !this.isEditing
    },
    addKeyListInner () {
      this.keyList.push({
        keyItem: '',
        keys: '',
        cards: '',
      })
    },
    isKeysRequired (index) {
      if (this.keyList[index].keyItem !== '' && this.keyList[index].cards === '') { return true }
      if (this.keyList[index].keys !== '') { return true }
      return false
    },
    isCardsRequired (index) {
      if (this.keyList[index].keyItem !== '' && this.keyList[index].keys === '') { return true }
      if (this.keyList[index].cards !== '') { return true }
      return false
    },
    changeItemValue (index, val, field) {
      this.keyList[index][field] = val
      formUtils.setDirty()
    },
    validate () {
      fusionInputCheckUtil.validateAllFields(undefined, '.key-schedule-edit')
      return fusionInputCheckUtil.isAllFieldsValid('.key-schedule-edit')
    },
    openDeleteConfirmModel (item, index) {
      this.$refs.deleteKeyConfirmModel.openModal((error) => {
        if (!error) {
          this.deleteKey(item, index)
        }
      })
    },
    deleteKey (item, index) {
      this.keyList.splice(index, 1)
    },
    async save () {
      if (!this.validate()) {
        return
      }
      this.triggerEditStatus()
      this.keyList.forEach((item, index) => {
        if (lodash.isEmpty(item.keyItem) && lodash.isEmpty(item.keys) && lodash.isEmpty(item.cards)) {
          this.keyList.splice(index, 1)
        }
      })
      let keyListUnEmpty = lodash.cloneDeep(this.keyList)
      this.updateFieldByPath({ path: ['transactions'], newValue: keyListUnEmpty })
      this.updateFieldByPath({ path: ['keySignatures'], newValue: this.$refs.keySignatureRef.keySignatures })
      await this.updateHotb({attrs: ['transactions', 'keySignatures']})
      formUtils.resetDirty()
    },
    async cancel () {
      this.triggerEditStatus()
      await this.loadHotb(this.$router.currentRoute.params.id)
      formUtils.resetDirty()
    },
    async generatePDF () {
      const imgUrls = this.$refs.keySignatureRef.getImgUrl()
      this.pdfId = await exportKeyHotb(this.currentHotb, imgUrls, this.regionName)
    },
  },
}
</script>

<style lang="scss">
.key-schedule-edit {
  position: relative;
  .content {
    table th{
      color: #0C3A58;
      box-shadow: 0 2px 4px 0 rgba(234, 234, 234, 0.5);
    }
    table th, td{
      padding: 0.625rem;
    }
    input::placeholder,
    textarea::placeholder {
      font-style: italic;
    }

    textarea, input {
      height: 40px;
      min-height: 40px;
      width: 100%;
      margin-bottom: 0;
    }

    span {
      display: inline-block;
      vertical-align: bottom;
    }
    .keyItem {
      width: 78%;
    }

    .key {
      width: 10%;
    }
    .handoverCards {
      width: 85%;
    }
    .handoverItem,
    .handoverCards,
    .handoverKeys {
      font-weight: 400;
    }
    .keyHandedOver {
      width: 12%;
      input {
        height: 20px;
      }
      input:checked:after {
        height: 19px;
        width: 19px;
        font-size: 18px;
      }
      .handoverCards {
        width: 100%;
        display: flex;
        align-items: center;
      }
      .delete {
        width: 11px;
        margin-left: 3px;
        font-size: 16px;
        color: #C3C3C3;
      }
    }
    .list {
      .keyHandedOver {
        height: 40px;
        line-height: 40px;
        text-align: center;
      }
    }
    tr {
      background: none;
    }
  }
  .footer {
    margin-bottom: 15px;
    .add {
      border-bottom: 1px solid #1A557D;
      cursor: pointer;
    }
  }
  .stick-save.stickyBtnClass {
    .btnGroup {
      margin-right: 15px;
    }
  }

  .btnGroup {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 15px;
    margin-right: -25px;
    pointer-events: auto;

    button {
      margin: 0;
      white-space: nowrap;
    }

    .generatePDF {
      width: 140px;
    }
  }

  .stick-save {
    position: absolute;
    text-align: right;
    width: 100%;
    padding: 15px 0;
    top: -153px;
    pointer-events: none;
  }

  .error-highlight {
    background-color: #f9dcda !important;
  }

  .key-header {
    background: #F2F6F8;
    padding: 15px;
    font-family: Verdana;
    font-size: 14px;

    .remarks {
      .key-title {
        font-weight: bold;
        line-height: 20px;
        height: 20px;
        margin-bottom: 5px;
      }
      .content {
        font-family: Verdana;
      }
    }
  }
  .key .errorTip {
    width: 250px;
  }

}

</style>
